import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaCheck, FaChevronLeft } from 'react-icons/fa';
import '../styles/SkinShopPage.css';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';
import { isSignedIn, rememberReturnPath, sanitizeReturnPath } from '../_helpers/safeRedirect.js';
import {
  SHOP_TYPES,
  itemsOfType,
  pickFeaturedItem,
  pickSecondaryDrops,
  shopType,
  shopTypeCounts,
} from '../_helpers/skinShop.js';
import { ensureFxPackages } from '../_helpers/fxPackages.js';
import { isFxSkin } from '../_helpers/fxItems.js';
import { prefersReducedMotion } from '../_helpers/reducedMotion.js';
import { useSkinShop } from '../hooks/useSkinShop.js';
import { useSkinTryOn } from '../hooks/useSkinTryOn.js';
import { useSkinPurchase } from '../hooks/useSkinPurchase.js';
import { useMediaQuery } from '../hooks/useMediaQuery.js';
import { useReduceEffects } from '../hooks/useReduceEffects.js';
import StreamPageHeader from './partial/stream/StreamPageHeader.jsx';
import NowPlayingCard from './partial/stream/NowPlayingCard.jsx';
import SkinTryOnPanel, { skinPurchaseError } from './partial/stream/SkinTryOnPanel.jsx';
import FxTryOnPanel from './partial/stream/FxTryOnPanel.jsx';
import { CardPreview, PaintPreview } from './partial/shop/ShopPreview.jsx';
import { ShopDropCard, ShopFeature, SkinTile } from './partial/shop/ShopItem.jsx';

/** Where Stripe Checkout brings a shop purchase back to (Tim's decision on ENG-823). */
export const SHOP_PATH = '/shop';

/** The shop has no playing song, so a try-on is held against this key until taken off. */
export const SHOP_SONG_KEY = 'shop';

/** From this width the shop uses its wide layout: the drops band and the sticky try-on panel (ENG-894). */
export const SHOP_WIDE_QUERY = '(min-width: 1600px)';

/** The locale key of each tab (`shop.tabs.*`). */
const TAB_KEYS = {
  all: 'all',
  now_playing_card: 'nowPlayingCard',
  name_paint: 'namePaint',
  vibe_set: 'vibeSet',
  boost_animation: 'boostAnimation',
};

/** The viewer's display name, for painting it with a name paint; null signed out or until read. */
const useOwnName = (signedIn) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [name, setName] = useState(null);
  useEffect(() => {
    if (!signedIn) return undefined;
    let cancelled = false;
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/me`)
      .then((me) => { if (!cancelled && typeof me?.displayName === 'string') setName(me.displayName); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [signedIn]);
  return name;
};

/**
 * Loads the FX packages of the shop's vibe sets and boost animations, so
 * their tile previews take the packages' colours (fxTint); re-renders once
 * they are in. A package that doesn't load keeps the rarity's colours.
 */
const useFxTints = (items) => {
  const [, setLoaded] = useState(0);
  const ids = useMemo(
    () => (items ?? []).filter((item) => isFxSkin(item.skin)).map((item) => item.skin.fxPackageId).sort().join(','),
    [items],
  );
  useEffect(() => {
    if (!ids) return undefined;
    let cancelled = false;
    ensureFxPackages(ids.split(',')).then(() => { if (!cancelled) setLoaded((n) => n + 1); });
    return () => { cancelled = true; };
  }, [ids]);
};

/** The page heading: Back to stream (from a stream), the title and the subtitle side by side. */
const ShopHeader = ({ from }) => {
  const { t } = useLocale();
  return (
    <header className="skin-shop-header">
      {from && (
        <Link to={from} className="skin-shop-back" data-testid="skin-shop-back">
          <FaChevronLeft className="rtl-mirror" aria-hidden="true" /> {t('shop.backToStream')}
        </Link>
      )}
      <div className="skin-shop-heading">
        <h1 className="skin-shop-title">{t('shop.title')}</h1>
        <p className="skin-shop-subtitle">{t('shop.subtitle')}</p>
      </div>
    </header>
  );
};

/**
 * The skins shop (ENG-823) at `/shop`: everything SongShake sells, from
 * `GET /public/skins` (ENG-822). Skins are SongShake's, not a band's, so
 * nothing here names or sends a band.
 *
 * The "Record Shop" design (ENG-894). Below 1600px: the heading, a red stage
 * banner with the featured item (pickFeaturedItem) beside its large live
 * preview, folder tabs with counts, and the grid of tiles, each with a live
 * preview of the item (ShopPreview). Try it on / Preview anywhere swaps the
 * banner into the try-on view, scrolled into sight. From 1600px
 * (SHOP_WIDE_QUERY, via useMediaQuery so only one layout is in the DOM): a
 * full-width drops band (the heading, the featured item and two secondary
 * drops, pickSecondaryDrops) and a sticky try-on panel beside the grid. Try it
 * on / Preview fills that panel without moving the page, and the tile on gets
 * marked; with nothing on, the panel shows the viewer's look.
 *
 * Signed out it is browse-only: Get it goes to /login and comes back here.
 * Signed in, owned skins say Owned. The tabs filter the list and are kept in
 * `?type=`.
 *
 * Try it on and Get it reuse the stream page's pieces (useSkinTryOn,
 * useSkinPurchase, SkinTryOnPanel in its shop look). With no song playing
 * here, a card skin is tried on a sample now-playing card and a name paint
 * paints the viewer's own name; it stays on until Take it off. Get it pays
 * with the saved card or on Stripe Checkout, which comes back to /shop. A
 * free skin (priceCents 0) says "Free" and "Get it free" gets it in one
 * request (ENG-827). Every Get it sends the price shown; if it changed
 * meanwhile, the page says so, shows the new price and waits for another
 * tap. A bought skin turns Owned, and the green notice says so.
 *
 * Vibe sets and boost animations (ENG-816) are sold here too, under their own
 * tabs. Preview plays one live on a sample album card on a stage of its own
 * (FxTryOnPanel), whether owned or not; tiles and drop cards loop a light CSS
 * version tinted from the package. Every loop and spin stops under reduce
 * motion and Photosensitivity mode (useReduceEffects).
 *
 * "See all" on another listener's profile card on the stream page lands here
 * with `state.from` set to that stream, so the page offers "Back to stream".
 */
const SkinShopPage = () => {
  const { t, format } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const type = shopType(searchParams.get('type'));
  const from = sanitizeReturnPath(location.state?.from);
  const signedIn = isSignedIn();
  const ownName = useOwnName(signedIn) || t('shop.yourName');
  const wide = useMediaQuery(SHOP_WIDE_QUERY);
  const still = useReduceEffects();
  const heroRef = useRef(null);

  const shop = useSkinShop();
  useFxTints(shop.items);
  const skinTryOn = useSkinTryOn(SHOP_SONG_KEY);
  const skinTryOnRef = useRef(skinTryOn);
  skinTryOnRef.current = skinTryOn;
  const itemsRef = useRef(shop.items);
  itemsRef.current = shop.items;
  const wideRef = useRef(wide);
  wideRef.current = wide;
  const [notice, setNotice] = useState(null);
  // The vibe set or boost animation being previewed (ENG-816); one try-on at a time.
  const [fxTry, setFxTry] = useState(null);

  const { markOwned, markSoldOut, retry, setPrice } = shop;
  const handlePurchased = useCallback((result) => {
    const item = itemsRef.current?.find((candidate) => candidate.id === result.skinId);
    markOwned(result.skinId);
    setNotice({
      name: item?.skin.name ?? '',
      editionNumber: result.editionNumber,
      editionSize: item?.editionSize ?? null,
    });
    skinTryOnRef.current.takeOff();
    setFxTry(null);
    // Fresh edition counts for everyone else's next copy.
    retry();
  }, [markOwned, retry]);
  // The price moved since the listener opened it (ENG-825/827): show the backend's new price on the
  // item at once, read the catalog and any try-on offer again, and wait for a fresh tap.
  const handlePriceChanged = useCallback(({ skinId, priceCents }) => {
    setPrice(skinId, priceCents);
    skinTryOnRef.current.refresh(skinId);
    retry();
  }, [setPrice, retry]);
  // Every edition went while the listener looked (409 skin_sold_out, ENG-833): show Sold out at
  // once, then read the catalog and any try-on offer again.
  const handleSoldOut = useCallback(({ skinId }) => {
    markSoldOut(skinId);
    if (skinId) skinTryOnRef.current.refresh(skinId);
    retry();
  }, [markSoldOut, retry]);
  const purchase = useSkinPurchase({
    onPurchased: handlePurchased,
    onPriceChanged: handlePriceChanged,
    onSoldOut: handleSoldOut,
    returnPath: SHOP_PATH,
  });
  const busy = purchase.state === 'buying' || purchase.state === 'redirecting';

  // `offer` is the shop item or the try-on's offer on screen; its priceCents is sent as is.
  const getIt = useCallback((skin, offer) => {
    if (!skin) return;
    if (!isSignedIn()) {
      rememberReturnPath(`${SHOP_PATH}${location.search}`);
      navigate('/login');
      return;
    }
    setNotice(null);
    purchase.buy(skin.id, { priceCents: offer?.priceCents, name: skin.name, onSale: Boolean(offer?.sale) });
  }, [location.search, navigate, purchase]);

  // A sale (ENG-832) or an item's sale window (ENG-834) ran out while on screen: read the catalog
  // (and any try-on offer) again, for the new price or to drop the item.
  const handleOfferEnd = useCallback(() => retry(), [retry]);
  const handleTryOnOfferEnd = useCallback((skinId) => skinTryOnRef.current.refresh(skinId), []);

  const tryItOn = useCallback((item) => {
    purchase.reset();
    setNotice(null);
    if (isFxSkin(item.skin)) {
      skinTryOnRef.current.takeOff();
      setFxTry(item);
    } else {
      setFxTry(null);
      skinTryOnRef.current.start(item.skin);
    }
    // Below 1600px the banner becomes the try-on: bring it into sight. The wide layout's panel
    // is sticky, so the page never moves there.
    if (!wideRef.current) {
      const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      window.requestAnimationFrame?.(() => heroRef.current?.scrollIntoView?.({ block: 'nearest', behavior }));
    }
  }, [purchase]);

  const takeOff = useCallback(() => {
    skinTryOnRef.current.takeOff();
    setFxTry(null);
    purchase.reset();
  }, [purchase]);

  const selectType = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('type');
    else params.set('type', next);
    setSearchParams(params, { replace: true, state: location.state });
  };

  const on = skinTryOn.tryOn;
  const visible = itemsOfType(shop.items, type);
  const counts = shop.items ? shopTypeCounts(shop.items) : null;
  const featured = pickFeaturedItem(shop.items);
  const drops = wide ? pickSecondaryDrops(shop.items, featured) : [];
  // The previewed item as the shop has it now (owned after a purchase).
  const fxOn = fxTry ? shop.items?.find((item) => item.id === fxTry.id) ?? fxTry : null;
  const tryingId = on?.skin.id ?? fxOn?.id ?? null;
  const sample = { title: t('shop.sampleSong.title'), artist: t('shop.sampleSong.artist'), requestedBy: ownName };

  const itemProps = { ownName, busy, onTryOn: tryItOn, onGetIt: getIt, onOfferEnd: handleOfferEnd };

  // What's on, in the shop's try-on look: the banner's (below 1600px) or the panel's (from 1600px).
  let tryView = null;
  if (on) {
    tryView = (
      <div className="skin-shop-try-on" data-testid="skin-shop-try-on">
        <SkinTryOnPanel
          variant="shop"
          tryOn={on}
          offer={skinTryOn.offer}
          purchase={purchase}
          onGetIt={getIt}
          onTakeOff={takeOff}
          onOfferEnd={handleTryOnOfferEnd}
          stage={(
            <div className="shop-stage shop-stage--large" aria-label={t('shop.previewLabel', { name: on.skin.name })} role="img">
              <div className="shop-stage-body">
                {on.look ? (
                  <NowPlayingCard song={sample} skin={on.look} decorative />
                ) : (
                  <PaintPreview paint={on.paint} ownName={ownName} large />
                )}
              </div>
            </div>
          )}
        />
      </div>
    );
  } else if (fxOn) {
    tryView = (
      <div className="skin-shop-try-on" data-testid="skin-shop-fx-try-on">
        <FxTryOnPanel
          variant="shop"
          item={fxOn}
          purchase={purchase}
          onGetIt={(item) => getIt(item.skin, item)}
          onClose={takeOff}
          onOfferEnd={handleOfferEnd}
        />
      </div>
    );
  }

  const notices = (
    <>
      {/* A purchase settling with nothing on (back from Checkout), and a bought skin. */}
      {!on && !fxOn && (purchase.error || purchase.state === 'buying') && (
        <p className="skin-shop-notice" role="status" data-testid="skin-shop-notice">
          {purchase.error ? skinPurchaseError(t, format, purchase) : t('stream.tryOn.buying')}
        </p>
      )}
      {notice && !purchase.error && purchase.state !== 'buying' && (
        <p className="skin-shop-notice skin-shop-notice--done" role="status" data-testid="skin-shop-notice">
          <FaCheck className="skin-shop-notice-icon" aria-hidden="true" />
          <span>
            {notice.editionNumber && notice.editionSize
              ? t('stream.tryOn.boughtEdition', {
                name: notice.name || t('stream.tryOn.yourSkin'),
                number: format.number(notice.editionNumber),
                size: format.number(notice.editionSize),
              })
              : t('stream.tryOn.bought', { name: notice.name || t('stream.tryOn.yourSkin') })}
          </span>
        </p>
      )}
    </>
  );

  const ready = Boolean(shop.items) && shop.status !== 'error';

  return (
    <div className={`skin-shop-page${wide ? ' skin-shop-page--wide' : ''}${still ? ' skin-shop-page--still' : ''}`}>
      <StreamPageHeader />

      {wide && (
        <section className="skin-shop-band" aria-label={t('shop.featured.bandLabel')} data-testid="skin-shop-band">
          <div className="skin-shop-wrap skin-shop-band-inner">
            <ShopHeader from={from} />
            {ready && (featured || drops.length > 0) && (
              <div className="skin-shop-drops">
                {featured && <ShopFeature key={featured.id} item={featured} {...itemProps} />}
                {drops.map(({ kind, item }) => (
                  <ShopDropCard key={`${kind}:${item.id}`} kind={kind} item={item} {...itemProps} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <main className="skin-shop skin-shop-wrap">
        {!wide && <ShopHeader from={from} />}

        {!wide && ready && (tryView || featured) && (
          <section
            ref={heroRef}
            className={`skin-shop-hero${tryView ? ' skin-shop-hero--try-on' : ''}`}
            aria-label={tryView ? t('shop.look.title') : t('shop.featured.kicker')}
            data-testid="skin-shop-hero"
          >
            {tryView || <ShopFeature key={featured.id} item={featured} {...itemProps} />}
          </section>
        )}

        <div className="skin-shop-layout">
          <div className="skin-shop-main">
            {notices}

            <div className="skin-shop-tabs" role="group" aria-label={t('shop.tabs.label')}>
              {SHOP_TYPES.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`skin-shop-tab${type === key ? ' skin-shop-tab--active' : ''}`}
                  aria-pressed={type === key}
                  onClick={() => selectType(key)}
                  data-testid={`skin-shop-tab-${key}`}
                >
                  <span>{t(`shop.tabs.${TAB_KEYS[key]}`)}</span>
                  {counts && (
                    <span className="skin-shop-tab-count" data-testid={`skin-shop-tab-count-${key}`}>
                      <span aria-hidden="true">{format.number(counts[key])}</span>
                      <span className="skin-shop-sr">{t('shop.tabs.count', { count: counts[key], number: format.number(counts[key]) })}</span>
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="skin-shop-body" aria-busy={shop.status === 'loading'}>
              {shop.status === 'loading' && !shop.items && (
                <p className="skin-shop-status" role="status">{t('shop.loading')}</p>
              )}

              {shop.status === 'error' && (
                <div className="skin-shop-status" role="alert">
                  <span>{t('shop.error')}</span>
                  <button type="button" className="skin-shop-action" onClick={retry}>{t('shop.retry')}</button>
                </div>
              )}

              {ready && (
                visible.length === 0 ? (
                  <p className="skin-shop-status" data-testid="skin-shop-empty">
                    {t(type === 'all' ? 'shop.empty' : 'shop.emptyType')}
                  </p>
                ) : (
                  <ul className="skin-shop-grid" data-testid="skin-shop-grid">
                    {visible.map((item) => {
                      switch (item.itemType) {
                        case 'skin':
                        case 'vibe_set':
                        case 'boost_animation':
                          return <SkinTile key={item.id} item={item} trying={wide && item.id === tryingId} {...itemProps} />;
                        default:
                          return null;
                      }
                    })}
                  </ul>
                )
              )}
            </div>
          </div>

          {wide && (
            <aside className="skin-shop-rail" aria-label={t('shop.look.title')} data-testid="skin-shop-look">
              {tryView || (
                <div className="skin-try-on skin-try-on--shop skin-shop-look-empty" data-testid="skin-shop-look-empty">
                  <div className="skin-try-on-stage">
                    <span className="skin-try-on-pill">{t('shop.look.now')}</span>
                    <div className="shop-stage shop-stage--large">
                      <div className="shop-stage-body">
                        <CardPreview skin={null} ownName={ownName} />
                      </div>
                    </div>
                  </div>
                  <div className="skin-try-on-info">
                    <h2 className="skin-try-on-name">{t('shop.look.title')}</h2>
                    <p className="skin-try-on-body">{t('shop.look.empty')}</p>
                    <p className="skin-shop-look-note">{t('shop.look.onlyYou')}</p>
                  </div>
                </div>
              )}
            </aside>
          )}
        </div>
      </main>
    </div>
  );
};

export default SkinShopPage;
